<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="note-spese-month-locks">
    <oxd-form-row>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="selectedMonth"
            type="select"
            :label="$t('time.month')"
            :options="monthOptions"
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="selectedYear"
            type="select"
            :label="$t('general.year')"
            :options="yearOptions"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <oxd-loading-spinner v-if="isLoading" />

    <table v-else-if="rows.length > 0" class="note-spese-month-locks-table">
      <thead>
        <tr>
          <th>{{ $t('general.name') }}</th>
          <th>{{ $t('noteSpese.month_locks') }}</th>
          <th class="note-spese-numeric">{{ $t('general.total') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.empNumber">
          <td>{{ row.firstName }} {{ row.lastName }}</td>
          <td>
            {{ statusLabel(row) }} · {{ entryCountLabel(row) }}
            <oxd-text
              v-if="row.missingRateCount > 0"
              tag="p"
              class="note-spese-missing-rate-warning"
            >
              {{ row.missingRateCount }} {{ $t('noteSpese.missing_rates') }}
            </oxd-text>
          </td>
          <td class="note-spese-numeric">{{ row.total }}</td>
          <td>
            <oxd-button
              v-if="!row.locked"
              display-type="ghost"
              :label="$t('noteSpese.lock_month')"
              @click="onLockClick(row)"
            />
            <oxd-button
              v-else
              display-type="ghost"
              :label="$t('noteSpese.reopen_month')"
              @click="onReopenClick(row)"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <oxd-text v-else>{{ $t('general.no_records_found') }}</oxd-text>

    <teleport to="#app">
      <oxd-dialog
        v-if="pendingAction"
        class="orangehrm-dialog-popup"
        @update:show="onCancelDialog"
      >
        <div class="orangehrm-modal-header">
          <oxd-text type="card-title">{{
            $t('general.are_you_sure')
          }}</oxd-text>
        </div>
        <div class="orangehrm-text-center-align">
          <oxd-text type="card-body">{{ dialogMessage }}</oxd-text>
        </div>
        <div class="orangehrm-modal-footer">
          <oxd-button
            :label="$t('general.cancel')"
            display-type="text"
            class="orangehrm-button-margin"
            @click="onCancelDialog"
          />
          <oxd-button
            :label="$t('general.yes')"
            :disabled="isSaving"
            display-type="secondary"
            class="orangehrm-button-margin"
            @click="onConfirmDialog"
          />
        </div>
      </oxd-dialog>
    </teleport>
  </div>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';
import useToast from '@/core/util/composable/useToast';

const MONTH_KEYS = [
  'general.january',
  'general.february',
  'general.march',
  'general.april',
  'general.may',
  'general.june',
  'general.july',
  'general.august',
  'general.september',
  'general.october',
  'general.november',
  'general.december',
];

export default {
  name: 'MonthLocks',

  components: {
    'oxd-dialog': OxdDialog,
  },

  setup() {
    const toast = useToast();

    const monthLocksHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/note-spese/month-locks',
    );

    return {
      toast,
      monthLocksHttp,
    };
  },

  data() {
    // Il mese di default è quello precedente: è quello che l'export
    // automatico ha appena chiuso (o chiuderà), lo stesso che HR vuole
    // controllare per primo aprendo la pagina.
    const now = new Date();
    const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    return {
      selectedMonth: {
        id: previous.getMonth() + 1,
        label: '',
      },
      selectedYear: {
        id: previous.getFullYear(),
        label: String(previous.getFullYear()),
      },
      rows: [],
      isLoading: false,
      isSaving: false,
      pendingAction: null,
    };
  },

  computed: {
    monthOptions() {
      return MONTH_KEYS.map((key, index) => ({
        id: index + 1,
        label: this.$t(key),
      }));
    },

    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let year = currentYear - 5; year <= currentYear + 1; year++) {
        years.push({id: year, label: String(year)});
      }
      return years;
    },

    dialogMessage() {
      if (!this.pendingAction) return '';

      return this.pendingAction.mode === 'reopen'
        ? 'Riaprendo, gli importi verranno ricalcolati con le tariffe attuali alla prossima chiusura.'
        : "Chiudendo il mese, tariffe e importi delle trasferte verranno congelati e l'amministratore non potrà più modificare le voci.";
    },
  },

  watch: {
    selectedMonth() {
      this.fetchRows();
    },
    selectedYear() {
      this.fetchRows();
    },
  },

  created() {
    this.selectedMonth.label = this.$t(MONTH_KEYS[this.selectedMonth.id - 1]);
    this.fetchRows();
  },

  methods: {
    async fetchRows() {
      if (!this.selectedMonth?.id || !this.selectedYear?.id) return;

      this.isLoading = true;
      try {
        const response = await this.monthLocksHttp.getAll({
          year: this.selectedYear.id,
          month: this.selectedMonth.id,
        });
        this.rows = response.data?.data ?? [];
      } finally {
        this.isLoading = false;
      }
    },

    // "Chiuso il ... da HR" non porta il nome di chi ha chiuso: l'API non lo
    // espone (solo l'id utente è salvato), quindi si distingue solo fra
    // l'estrazione automatica e un intervento manuale.
    statusLabel(row) {
      if (!row.locked) {
        return 'Aperto';
      }
      const when = row.lockedAt ?? '';
      return row.lockedByNotification
        ? `Chiuso il ${when} (automatico)`
        : `Chiuso il ${when} da HR`;
    },

    // Nessuna chiave di lingua dedicata per "N voci": è un numero soltanto,
    // messo accanto allo stato invece che in una colonna propria.
    entryCountLabel(row) {
      return row.tripCount + row.expenseCount;
    },

    onLockClick(row) {
      this.pendingAction = {mode: 'lock', row};
    },

    onReopenClick(row) {
      this.pendingAction = {mode: 'reopen', row};
    },

    onCancelDialog() {
      this.pendingAction = null;
    },

    async onConfirmDialog() {
      if (!this.pendingAction) return;
      const {mode, row} = this.pendingAction;

      this.isSaving = true;
      try {
        // Un fallimento (422 per trasferte senza tariffa, ecc.) lo mostra
        // già l'interceptor globale di APIService: il dialog resta aperto
        // cosi' l'utente può leggerlo e riprovare o annullare.
        await this.monthLocksHttp.update(row.empNumber, {
          year: this.selectedYear.id,
          month: this.selectedMonth.id,
          locked: mode === 'lock',
        });
        this.toast.updateSuccess();
        this.pendingAction = null;
        await this.fetchRows();
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.note-spese-month-locks-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  margin-top: 1rem;

  th,
  td {
    text-align: left;
    padding: 0.5rem;
    border-bottom: 1px solid #e0e0e0;
  }
}

.note-spese-numeric {
  text-align: right;
}

.note-spese-missing-rate-warning {
  color: #b06d00;
  font-size: 0.8rem;
  margin: 0.15rem 0 0;
}
</style>
